import { IImageData, ImageDisplayType, ImageRecord, toFormData } from "common/records/Image";
import { baseCleanProps } from "common/api/Api";
import { fetcher, pricelistFetcher } from "common/api/Fetcher";
import { IMetaPaginationData } from "common/records/MetaPagination";
import { toParams } from "common/records/index/QueryParamsBase";
import { ImageParamsRecord } from "common/records/index/ImageParams";
import { Nullable } from "common/types";
import { List } from "immutable";
import { IDirectUploadResponse } from "common/api/directUpload.service";

export interface IImageIndexResponse {
  images: IImageData[];
  meta: { pagination: IMetaPaginationData };
}

export interface IImageResponse {
  image?: IImageData;
  images?: IImageData[];
}

export type ImageableType =
  | "Org"
  | "Job"
  | "Product"
  | "Token"
  | "ProductOption"
  | "EstimateLineItem"
  | "EstimateTemplate";
export interface IImageOptions {
  imageableType: ImageableType;
  imageableId?: Nullable<number>;
  image_ids?: List<number>;
  folder_id?: number;
  display?: ImageDisplayType;
  refreshLoad?: boolean;
  sort_order?: Nullable<number>;
  /** Staging records to drop from `images.byId` on upload success, paired positionally with `files`. */
  replacedNegativeIds?: number[];
}

export interface ICustomerAuthOptions {
  token: string;
  key: string;
}

const urls = {
  Org: "api/v1/orgs/:id/images",
  Job: "api/v1/jobs/:id/images",
  Product: "api/v1/products/:id/images",
  Customer: "api/v1/images",
  Token: "api/v1/tokens/:id/images",
  ProductOption: "api/v1/product_options/:id/images",
  EstimateLineItem: "api/v1/estimate_line_items/:id/images",
  EstimateTemplate: "api/v1/estimate_templates/:id/images",
};

export const list = (queryParams: ImageParamsRecord): Promise<IImageIndexResponse> => {
  const imageableId = queryParams.imageableId?.toString() ?? "0";
  const actionUrl = urls[queryParams.imageableType].replace(":id", imageableId);
  const params = toParams(queryParams);

  delete params.imageableId;
  delete params.imageableType;

  return getFetcher(queryParams.imageableType).get<IImageIndexResponse>(actionUrl, params);
};

export const updateOrCreate = (image: ImageRecord): Promise<IImageResponse> => {
  if (image.id > 0) {
    return update(image);
  } else {
    return create(image);
  }
};

export const create = (image: ImageRecord): Promise<IImageResponse> => {
  const actionUrl = urlSetup(image);
  return getFetcher(image.imageable_type).post<IImageResponse>(actionUrl, toFormData(image), { formData: true });
};

export const directUpload = (
  fileCount: number,
  imageableType: ImageableType,
  imageableId: number,
): Promise<IDirectUploadResponse> => {
  const f = getFetcher(imageableType);
  const actionUrl = f.joinUrls(urls[imageableType].replace(":id", imageableId.toString()), "direct");
  return f.post(actionUrl, { count: fileCount });
};

export const createDirect = (image: ImageRecord): Promise<IImageResponse> => {
  const actionUrl = urlSetup(image);
  return getFetcher(image.imageable_type).post(actionUrl, cleanProps(image));
};

export const createForCustomer = async (
  image: ImageRecord,
  authOptions: ICustomerAuthOptions,
): Promise<IImageResponse> => {
  const actionUrl = fetcher.joinUrls(urls["Customer"], authOptions.key);
  const cleanImage = cleanProps(image);

  const response = await fetch(actionUrl, {
    body: JSON.stringify({ image: cleanImage, token: authOptions.token }),
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw response;
  }
  return response.json();
};

export const update = (image: ImageRecord): Promise<IImageResponse> => {
  const f = getFetcher(image.imageable_type);
  const actionUrl = f.joinUrls(urlSetup(image), image.id.toString());
  const requestBody = cleanProps(image);
  delete requestBody.file;
  return f.patch(actionUrl, requestBody);
};

export const destroy = (image: ImageRecord): Promise<IImageResponse> => {
  const f = getFetcher(image.imageable_type);
  const actionUrl = f.joinUrls(urlSetup(image), image.id.toString());
  return f.delete(actionUrl);
};

export const cleanProps = (image: ImageRecord): Partial<IImageData> => {
  let imageParams = image.toJS() as any;
  imageParams = baseCleanProps(imageParams);

  if (imageParams.folder_id === null || imageParams.folder_id === undefined || imageParams.folder_id === 0) {
    delete imageParams.folder_id;
  }

  delete imageParams.displayInProposal;
  delete imageParams.displayInAgreement;
  // Client-only dedup key (see Image.ts IImageRecord.source_image_id) — never sent to the server.
  delete imageParams.source_image_id;

  return imageParams;
};

export const getFetcher = (type: Nullable<ImageableType>) => {
  if (type === "Product") {
    return pricelistFetcher();
  }
  return fetcher;
};

export const urlSetup = (image: ImageRecord): string => {
  return urls[image.imageable_type!].replace(":id", image.imageable_id!.toString());
};
